import { Link, useMatchRoute } from '@tanstack/react-router';
import { useDroppable } from '@dnd-kit/core';
import clsx from 'clsx';
import Icon, { IconProps } from '../ui/Icon';
import type { ViewMode } from '../../types/api';
import type { DropTargetData } from '../../contexts/BookmarkDndContext';
import { formatCount } from '../../utils/numberUtils';

export interface NavItemProps {
  to: string;
  icon?: IconProps['name'];
  label: string;
  count?: number;
  mode?: ViewMode;
  matchMode?: ViewMode;
  isDropTarget?: boolean;
  useUrlMatch?: boolean;
  dropTargetType?: 'home' | 'archive' | 'liked';
  className?: string;
}

function NavItem({
  to,
  icon,
  label,
  count,
  mode,
  matchMode,
  isDropTarget = false,
  useUrlMatch = false,
  dropTargetType,
  className,
}: NavItemProps) {
  const matchRoute = useMatchRoute();

  const isActive = useUrlMatch ? matchRoute({ to, fuzzy: false }) : mode === matchMode;
  const shouldBeDroppable = isDropTarget && dropTargetType && !isActive;

  const dropData: DropTargetData | undefined = dropTargetType
    ? { type: dropTargetType }
    : undefined;

  const { setNodeRef, isOver } = useDroppable({
    id: `nav-${dropTargetType || label.toLowerCase()}`,
    data: dropData,
    disabled: !shouldBeDroppable,
  });

  return (
    <Link
      ref={setNodeRef}
      to={to}
      className={clsx(
        'side-item',
        shouldBeDroppable && 'transition-all',
        isOver && shouldBeDroppable && 'drop-target-highlight',
        className,
      )}
      data-active={isActive ? 'true' : undefined}
      data-droptarget={shouldBeDroppable ? 'true' : undefined}
      draggable={false}
    >
      {icon && (
        <>
          <Icon name={icon} />{' '}
        </>
      )}
      <span>{label}</span>
      {typeof count === 'number' && count > 0 && (
        <span className="side-nav-count" title={String(count)}>
          {formatCount(count)}
        </span>
      )}
    </Link>
  );
}

export default NavItem;
